import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { programs, programsCategories, programsMetricsSummary, type Program, type ProgramCategory } from '@/mocks/programs';

type LoadingState = 'loading' | 'ready' | 'error';

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function simulateLoad<T>(data: T): Promise<T> {
  return new Promise((resolve, reject) => {
    const d = 600 + Math.random() * 400;
    setTimeout(() => {
      if (Math.random() > 0.96) {
        reject(new Error('Timeout al conectar con el servidor.'));
      } else {
        resolve(data);
      }
    }, d);
  });
}

/* ── Category Badge ── */
const categoryConfig: Record<ProgramCategory, { bg: string; text: string; icon: string }> = {
  Música: { bg: 'bg-violet-50', text: 'text-violet-700', icon: 'ri-music-2-line' },
  Deportes: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-football-line' },
  Noticias: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'ri-newspaper-line' },
  Entretenimiento: { bg: 'bg-rose-50', text: 'text-rose-700', icon: 'ri-tv-2-line' },
  Cultura: { bg: 'bg-sky-50', text: 'text-sky-700', icon: 'ri-palette-line' },
  Educación: { bg: 'bg-teal-50', text: 'text-teal-700', icon: 'ri-book-open-line' },
  Salud: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-heart-pulse-line' },
  Tecnología: { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: 'ri-computer-line' },
  Finanzas: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'ri-money-dollar-circle-line' },
  Comedia: { bg: 'bg-orange-50', text: 'text-orange-700', icon: 'ri-emotion-laugh-line' },
};

function CategoryBadge({ category }: { category: ProgramCategory }) {
  const cfg = categoryConfig[category] ?? { bg: 'bg-background-100', text: 'text-foreground-500', icon: 'ri-price-tag-3-line' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} text-[10px] font-medium whitespace-nowrap`}>
      <i className={`${cfg.icon} text-[9px]`}></i>
      {category}
    </span>
  );
}

/* ── Active Status Badge ── */
function ActiveBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-medium whitespace-nowrap">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
      Al aire
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-500 text-[10px] font-medium whitespace-nowrap">
      <span className="w-1.5 h-1.5 rounded-full bg-foreground-400"></span>
      Inactivo
    </span>
  );
}

/* ── KPI Card ── */
function KpiCard({ label, value, icon, bg, color }: { label: string; value: string; icon: string; bg: string; color: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 hover:border-background-300 transition-colors">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <i className={`${icon} ${color} text-lg`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-foreground-400 text-[10px] font-medium truncate">{label}</p>
        <p className="text-foreground-800 text-lg font-bold leading-tight">{value}</p>
      </div>
    </div>
  );
}

/* ── Filter Chip ── */
function FilterChip({ label, active, onClick, count }: { label: string; active: boolean; onClick: () => void; count: number }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-foreground-950 text-background-50'
          : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300 hover:text-foreground-700'
      }`}
    >
      {label}
      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
        active ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'
      }`}>{count}</span>
    </button>
  );
}

/* ═══════════════════════════════════════════════
   MODALS
   ═══════════════════════════════════════════════ */

/* ── View / Detail Modal ── */
function ViewProgramModal({ program, onClose }: { program: Program; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-lg">
        {/* Cover Image */}
        <div className="relative w-full h-48 rounded-t-2xl overflow-hidden">
          <img src={program.coverUrl} alt={program.name} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
          <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between gap-4">
            <div>
              <h3 className="text-white font-bold text-base drop-shadow-sm">{program.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <CategoryBadge category={program.category as ProgramCategory} />
                <ActiveBadge active={program.isActive} />
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0 cursor-pointer hover:bg-white/30 transition-colors"
            >
              <i className="ri-close-line text-white text-sm"></i>
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Description */}
          <div>
            <h4 className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide mb-2">Descripción</h4>
            <p className="text-foreground-700 text-xs leading-relaxed">{program.description}</p>
          </div>

          {/* Schedule & Radio */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Horario</p>
              <p className="text-foreground-800 text-xs flex items-center gap-1">
                <i className="ri-time-line text-foreground-400 text-[10px]"></i>
                {program.schedule}
              </p>
              <p className="text-foreground-400 text-[9px] mt-0.5">{program.timezone}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Radio</p>
              <p className="text-foreground-800 text-xs font-semibold">{program.radioName}</p>
              <p className="text-foreground-400 text-[9px]">{program.radioFrequency}</p>
            </div>
          </div>

          {/* Community KPIs */}
          <div>
            <h4 className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide mb-2">Comunidad</h4>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-background-100 rounded-xl p-3 text-center">
                <div className="w-8 h-8 mx-auto mb-1 rounded-lg bg-primary-50 flex items-center justify-center">
                  <i className="ri-user-heart-line text-primary-500 text-sm"></i>
                </div>
                <p className="text-foreground-800 text-sm font-bold">{formatNumber(program.community.followers)}</p>
                <p className="text-foreground-400 text-[9px]">Seguidores</p>
              </div>
              <div className="bg-background-100 rounded-xl p-3 text-center">
                <div className="w-8 h-8 mx-auto mb-1 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <i className="ri-chat-1-line text-emerald-500 text-sm"></i>
                </div>
                <p className="text-foreground-800 text-sm font-bold">{formatNumber(program.community.comments)}</p>
                <p className="text-foreground-400 text-[9px]">Comentarios</p>
              </div>
              <div className="bg-background-100 rounded-xl p-3 text-center">
                <div className="w-8 h-8 mx-auto mb-1 rounded-lg bg-amber-50 flex items-center justify-center">
                  <i className="ri-share-forward-line text-amber-500 text-sm"></i>
                </div>
                <p className="text-foreground-800 text-sm font-bold">{formatNumber(program.community.shares)}</p>
                <p className="text-foreground-400 text-[9px]">Compartidos</p>
              </div>
            </div>
          </div>

          {/* Hosts */}
          <div>
            <h4 className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide mb-2">Locutores</h4>
            <div className="space-y-2">
              {program.hosts.map((host) => (
                <div key={host.id} className="bg-background-100 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border-2 border-background-200">
                    <img src={host.photoUrl} alt={host.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground-800 text-xs font-semibold">{host.name}</p>
                    <p className="text-foreground-400 text-[9px]">{host.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div>
            <h4 className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide mb-2">Etiquetas</h4>
            <div className="flex items-center gap-1.5 flex-wrap">
              {program.tags.map((tag) => (
                <span key={tag} className="px-2 py-1 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium">
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Created date */}
          <div className="flex items-center gap-2 text-foreground-400 text-[10px]">
            <i className="ri-calendar-line text-[10px]"></i>
            <span>Creado en RFW: {program.createdAt}</span>
          </div>
        </div>

        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit Program Modal ── */
function EditProgramModal({ program, onClose, onSave }: { program: Program; onClose: () => void; onSave: (data: Partial<Program>) => void }) {
  const [formData, setFormData] = useState({
    name: program.name,
    category: program.category,
    isActive: program.isActive,
    schedule: program.schedule,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = () => {
    setSaving(true);
    setTimeout(() => {
      onSave(formData);
      setSaving(false);
      onClose();
    }, 600);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-lg">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-center justify-between gap-4 z-10">
          <div>
            <h3 className="text-foreground-950 font-bold text-sm">Editar Programa</h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">{program.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors">
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Nombre</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Categoría</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
            >
              {programsCategories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Horario</label>
            <input
              type="text"
              value={formData.schedule}
              onChange={(e) => setFormData((p) => ({ ...p, schedule: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Estado</label>
            <select
              value={formData.isActive ? 'activo' : 'inactivo'}
              onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.value === 'activo' }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
            >
              <option value="activo">Al aire</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </div>
        </div>

        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.name.trim()}
            className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Program Card ── */
function ProgramCard({ program, onView, onEdit }: { program: Program; onView: (p: Program) => void; onEdit: (p: Program) => void }) {
  const [showActions, setShowActions] = useState(false);

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden hover:border-background-300 transition-all group">
      {/* Cover */}
      <div className="relative w-full h-36 overflow-hidden">
        <img src={program.coverUrl} alt={program.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <ActiveBadge active={program.isActive} />
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-white font-bold text-xs drop-shadow-sm line-clamp-1">{program.name}</p>
        </div>
      </div>

      <div className="p-3.5 space-y-3">
        {/* Schedule & Radio */}
        <div className="flex items-center gap-2 text-foreground-400 text-[9px]">
          <span className="flex items-center gap-1">
            <i className="ri-time-line text-[8px]"></i>
            {program.schedule}
          </span>
          <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
          <span className="flex items-center gap-1 truncate">
            <i className="ri-radio-line text-[8px]"></i>
            {program.radioName}
          </span>
        </div>

        {/* Description excerpt */}
        <p className="text-foreground-500 text-[10px] leading-relaxed line-clamp-2">{program.description}</p>

        {/* Hosts */}
        <div className="flex items-center gap-1">
          {program.hosts.slice(0, 3).map((host) => (
            <div key={host.id} className="w-6 h-6 rounded-full overflow-hidden border border-background-200" title={host.name}>
              <img src={host.photoUrl} alt={host.name} className="w-full h-full object-cover" />
            </div>
          ))}
          {program.hosts.length > 3 && (
            <span className="text-foreground-400 text-[9px] ml-1">+{program.hosts.length - 3}</span>
          )}
          <span className="text-foreground-400 text-[9px] ml-1.5 truncate">
            {program.hosts.map((h) => h.name.split(' ')[0]).join(', ')}
          </span>
        </div>

        {/* Community Stats Grid */}
        <div className="grid grid-cols-3 gap-1.5">
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <p className="text-foreground-800 text-[11px] font-bold">{formatNumber(program.community.followers)}</p>
            <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5">
              <i className="ri-user-heart-line text-[7px]"></i>Seg.
            </p>
          </div>
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <p className="text-foreground-800 text-[11px] font-bold">{formatNumber(program.community.comments)}</p>
            <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5">
              <i className="ri-chat-1-line text-[7px]"></i>Com.
            </p>
          </div>
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <p className="text-foreground-800 text-[11px] font-bold">{formatNumber(program.community.shares)}</p>
            <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5">
              <i className="ri-share-forward-line text-[7px]"></i>Comp.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-1 border-t border-background-100">
          <CategoryBadge category={program.category as ProgramCategory} />
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className={`${showActions ? 'ri-close-line' : 'ri-more-2-line'} text-foreground-500 text-xs`}></i>
            </button>
            {showActions && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)}></div>
                <div className="absolute right-0 bottom-full mb-1 z-20 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1 min-w-[120px]">
                  <button onClick={() => { setShowActions(false); onView(program); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-eye-line text-sm"></i>Ver perfil
                  </button>
                  <button onClick={() => { setShowActions(false); onEdit(program); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-edit-line text-sm"></i>Editar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Loading Skeleton ── */
function LoadingSkeleton() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
            <div className="w-10 h-10 rounded-xl bg-background-200"></div>
            <div className="space-y-1">
              <div className="w-16 h-2.5 bg-background-200 rounded"></div>
              <div className="w-10 h-4 bg-background-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden animate-pulse">
            <div className="w-full h-36 bg-background-200"></div>
            <div className="p-3.5 space-y-3">
              <div className="w-3/4 h-2.5 bg-background-200 rounded"></div>
              <div className="w-full h-6 bg-background-200 rounded"></div>
              <div className="flex gap-1">
                <div className="w-6 h-6 rounded-full bg-background-200"></div>
                <div className="w-6 h-6 rounded-full bg-background-200"></div>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {Array.from({ length: 3 }).map((_, j) => (
                  <div key={j} className="h-10 bg-background-200 rounded-lg"></div>
                ))}
              </div>
              <div className="flex justify-between">
                <div className="w-16 h-5 bg-background-200 rounded-full"></div>
                <div className="w-7 h-7 bg-background-200 rounded-lg"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Error State ── */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
        <i className="ri-error-warning-line text-red-400 text-2xl"></i>
      </div>
      <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar</p>
      <p className="text-foreground-400 text-xs mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
      >
        <i className="ri-refresh-line text-sm"></i>
        Reintentar
      </button>
    </div>
  );
}

/* ── Empty State ── */
function EmptyState({ onClear }: { onClear: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
        <i className="ri-mic-line text-foreground-300 text-xl"></i>
      </div>
      <p className="text-foreground-500 text-sm font-medium">Sin resultados</p>
      <p className="text-foreground-300 text-xs mt-1">No hay programas que coincidan con los filtros</p>
      <button onClick={onClear} className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer">
        <i className="ri-filter-off-line text-xs"></i>
        Limpiar filtros
      </button>
    </div>
  );
}

/* ── Summary Bar ── */
function SummaryBar() {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
          <i className="ri-mic-line text-primary-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Total Programas</p>
          <p className="text-foreground-800 text-sm font-bold">{programsMetricsSummary.totalPrograms}</p>
        </div>
      </div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
          <i className="ri-user-heart-line text-emerald-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Seguidores Totales</p>
          <p className="text-foreground-800 text-sm font-bold">{formatNumber(programsMetricsSummary.totalFollowers)}</p>
        </div>
      </div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
          <i className="ri-chat-1-line text-amber-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Comentarios Totales</p>
          <p className="text-foreground-800 text-sm font-bold">{formatNumber(programsMetricsSummary.totalComments)}</p>
        </div>
      </div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center">
          <i className="ri-share-forward-line text-rose-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Compartidos Totales</p>
          <p className="text-foreground-800 text-sm font-bold">{formatNumber(programsMetricsSummary.totalShares)}</p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════ */

export default function SAPrograms() {
  const [programList, setProgramList] = useState<Program[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<ProgramCategory | 'todas'>('todas');
  const [statusFilter, setStatusFilter] = useState<'todas' | 'activo' | 'inactivo'>('todas');
  const [searchTerm, setSearchTerm] = useState('');

  const [viewProgram, setViewProgram] = useState<Program | null>(null);
  const [editProgram, setEditProgram] = useState<Program | null>(null);

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad(programs)
      .then((data) => {
        setProgramList(data);
        setLoadingState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredPrograms = useMemo(() => {
    let result = programList;
    if (categoryFilter !== 'todas') {
      result = result.filter((p) => p.category === categoryFilter);
    }
    if (statusFilter === 'activo') {
      result = result.filter((p) => p.isActive);
    } else if (statusFilter === 'inactivo') {
      result = result.filter((p) => !p.isActive);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.radioName.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q)) ||
          p.hosts.some((h) => h.name.toLowerCase().includes(q)),
      );
    }
    return result;
  }, [programList, categoryFilter, statusFilter, searchTerm]);

  /* Counts */
  const statusCounts = useMemo(() => ({
    todas: programList.length,
    activo: programList.filter((p) => p.isActive).length,
    inactivo: programList.filter((p) => !p.isActive).length,
  }), [programList]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { todas: programList.length };
    programsCategories.forEach((cat) => {
      counts[cat] = programList.filter((p) => p.category === cat).length;
    });
    return counts;
  }, [programList]);

  /* Visible categories (only show ones with items) */
  const visibleCategories = useMemo(() =>
    programsCategories.filter((cat) => categoryCounts[cat] > 0),
  [categoryCounts]);

  const filteredMetrics = useMemo(() => ({
    total: filteredPrograms.length,
    followers: filteredPrograms.reduce((s, p) => s + p.community.followers, 0),
    comments: filteredPrograms.reduce((s, p) => s + p.community.comments, 0),
    shares: filteredPrograms.reduce((s, p) => s + p.community.shares, 0),
  }), [filteredPrograms]);

  const handleEditSave = useCallback((data: Partial<Program>) => {
    if (!editProgram) return;
    setProgramList((prev) =>
      prev.map((p) => (p.id === editProgram.id ? { ...p, ...data } : p))
    );
  }, [editProgram]);

  const clearAllFilters = () => {
    setCategoryFilter('todas');
    setStatusFilter('todas');
    setSearchTerm('');
  };

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Programas</span>
            </div>

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-[10px] font-semibold mb-2">
                  <i className="ri-mic-line text-xs"></i>
                  Bloque SA-52
                </div>
                <h2 className="text-foreground-950 font-bold text-lg">Programas</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  Programas como entidad comunitaria · {loadingState === 'ready' ? `${programList.length} programas en ${programsMetricsSummary.uniqueRadios} radios` : 'Cargando...'}
                </p>
              </div>
              <div className="relative w-full sm:w-64">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar programa, locutor, etiqueta..."
                  disabled={loadingState !== 'ready'}
                  className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
                  >
                    <i className="ri-close-line text-foreground-500 text-[10px]"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Summary Bar */}
            {loadingState === 'ready' && <SummaryBar />}

            {/* KPIs */}
            {loadingState === 'ready' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <KpiCard label="Programas" value={String(filteredMetrics.total)} icon="ri-mic-line" bg="bg-primary-50" color="text-primary-500" />
                <KpiCard label="Seguidores" value={formatNumber(filteredMetrics.followers)} icon="ri-user-heart-line" bg="bg-emerald-50" color="text-emerald-500" />
                <KpiCard label="Comentarios" value={formatNumber(filteredMetrics.comments)} icon="ri-chat-1-line" bg="bg-amber-50" color="text-amber-500" />
                <KpiCard label="Compartidos" value={formatNumber(filteredMetrics.shares)} icon="ri-share-forward-line" bg="bg-rose-50" color="text-rose-500" />
                <KpiCard label="Al Aire" value={String(programsMetricsSummary.totalActive)} icon="ri-broadcast-line" bg="bg-emerald-50" color="text-emerald-500" />
                <KpiCard label="Categorías" value={String(programsCategories.length)} icon="ri-stack-line" bg="bg-violet-50" color="text-violet-500" />
              </div>
            )}

            {/* Loading KPIs */}
            {loadingState === 'loading' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
                    <div className="w-10 h-10 rounded-xl bg-background-200"></div>
                    <div className="space-y-1">
                      <div className="w-16 h-2.5 bg-background-200 rounded"></div>
                      <div className="w-10 h-4 bg-background-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Filters */}
            {loadingState === 'ready' && (
              <div className="space-y-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-foreground-400 text-[9px] font-medium mr-1">Estado:</span>
                  <FilterChip label="Todas" active={statusFilter === 'todas'} onClick={() => setStatusFilter('todas')} count={statusCounts.todas} />
                  <FilterChip label="Al aire" active={statusFilter === 'activo'} onClick={() => setStatusFilter('activo')} count={statusCounts.activo} />
                  <FilterChip label="Inactivos" active={statusFilter === 'inactivo'} onClick={() => setStatusFilter('inactivo')} count={statusCounts.inactivo} />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-foreground-400 text-[9px] font-medium mr-1">Categoría:</span>
                  <button
                    onClick={() => setCategoryFilter('todas')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      categoryFilter === 'todas' ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300'
                    }`}
                  >
                    Todas
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${categoryFilter === 'todas' ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{categoryCounts.todas}</span>
                  </button>
                  {visibleCategories.map((cat) => {
                    const cfg = categoryConfig[cat];
                    const isActive = categoryFilter === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => setCategoryFilter(cat)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                          isActive
                            ? `${cfg.bg} ${cfg.text} border border-current/20`
                            : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300'
                        }`}
                      >
                        <i className={`${cfg.icon} text-[10px]`}></i>
                        {cat}
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                          isActive ? 'bg-current/10' : 'bg-background-100 text-foreground-400'
                        }`}>{categoryCounts[cat]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Cards Grid */}
            {loadingState === 'loading' && <LoadingSkeleton />}

            {loadingState === 'error' && <ErrorState message={errorMsg || 'Error'} onRetry={loadData} />}

            {loadingState === 'ready' && filteredPrograms.length === 0 && (
              <EmptyState onClear={clearAllFilters} />
            )}

            {loadingState === 'ready' && filteredPrograms.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredPrograms.map((program) => (
                  <ProgramCard
                    key={program.id}
                    program={program}
                    onView={setViewProgram}
                    onEdit={setEditProgram}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile bottom hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Programas</p>
      </div>

      {/* Modals */}
      {viewProgram && <ViewProgramModal program={viewProgram} onClose={() => setViewProgram(null)} />}
      {editProgram && <EditProgramModal program={editProgram} onClose={() => setEditProgram(null)} onSave={handleEditSave} />}
    </div>
  );
}